
import styled from 'styled-components';
import Navigation from '../../navigation/header';
import CTASection from '../../landing/landing_home/empower';
import Footer from '../../Footer/footer';


const Card = styled.main`
  display: flex;
  width: 100%;
  max-width: 1120px;
  padding: 56px;
  flex-direction: column;
  align-items: flex-start;
  gap: 64px;
  border-radius: 24px;
  margin: 0 auto;
  box-sizing: border-box;
  background-color: #fff;
  @media (max-width: 991px) {
    padding: 40px;
    gap: 48px;
    border-radius: 20px;
  }
  @media (max-width: 640px) {
    padding: 24px;
    gap: 32px;
    border-radius: 16px;
  }
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 42px;
  width: 100%;
  @media (max-width: 991px) {
    gap: 36px;
  }
  @media (max-width: 640px) {
    gap: 28px;
  }
`;

const HeaderSection = styled.header`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 32px;
  width: 100%;
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
`;

const MainTitle = styled.h1`
  color: #1c1b1f;
  letter-spacing: -0.32px;
  font: 700 32px/120% Inter, -apple-system, Roboto, Helvetica, sans-serif;
  margin: 0;
  @media (max-width: 991px) {
    font-size: 28px;
    line-height: 120%;
  }
  @media (max-width: 640px) {
    font-size: 24px;
    line-height: 120%;
    letter-spacing: -0.24px;
  }
`;

const OverviewSection = styled.section`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  width: 100%;
  @media (max-width: 640px) {
    gap: 16px;
  }
`;

const OverviewHeading = styled.h2`
  width: 100%;
  color: #64636a;
  letter-spacing: -0.18px;
  font: 700 18px/140% Inter, -apple-system, Roboto, Helvetica, sans-serif;
  margin: 0;
  @media (max-width: 991px) {
    font-size: 16px;
    line-height: 140%;
  }
  @media (max-width: 640px) {
    font-size: 16px;
    line-height: 140%;
  }
`;

const OverviewText = styled.p`
  width: 100%;
  color: #64636a;
  letter-spacing: -0.08px;
  font: 400 16px/160% Inter, -apple-system, Roboto, Helvetica, sans-serif;
  margin: 0;
  @media (max-width: 991px) {
    font-size: 15px;
    line-height: 160%;
  }
  @media (max-width: 640px) {
    font-size: 14px;
    line-height: 160%;
  }
`;

const ContactText = styled.p`
  width: 100%;
  color: #64636a;
  letter-spacing: -0.08px;
  font: 400 16px/160% Inter, -apple-system, Roboto, Helvetica, sans-serif;
  margin: 0;
  @media (max-width: 991px) {
    font-size: 15px;
    line-height: 160%;
  }
  @media (max-width: 640px) {
    font-size: 14px;
    line-height: 160%;
  }
`;

function PrivacyPolicy() {
  return (
    <>
      <link
        href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap"
        rel="stylesheet"
      />
       <PageBackground>
        <Navigation/>
      <Card>
        <ContentContainer>
          <HeaderSection>
            <TitleContainer>
              <MainTitle>Privacy Policy</MainTitle>
            </TitleContainer>
            <OverviewSection>
              <OverviewHeading>1. Overview</OverviewHeading>
              <OverviewText>
                Campus Insights is committed to user privacy and responsible
                technology use. Our platform is purpose-built for higher education
                institutions to enhance student engagement and decision-making
                through AI, without compromising privacy.
              </OverviewText>
              <OverviewText>
                We do not collect, store, or retain personally identifiable
                student data. Our platform is designed to operate without
                accessing educational records and aligns with institutional
                compliance standards, including FERPA.
              </OverviewText>
            </OverviewSection>
          </HeaderSection>

          <PolicySection
            title="2. Data We Do Not Collect"
            bulletPoints={[
              "Names, student IDs, or contact details",
              "Social Security numbers, financial or health data",
              "Educational records or academic performance details"
            ]}
          >
            Campus Insights does not collect:
          </PolicySection>

          <OverviewText>
            Our chatbot operates independently of personally identifiable data
            by default. Any user inputs are processed ephemerally and are not
            stored or retained by us after the session ends.
          </OverviewText>

          <PolicySection
            title="3. Anonymous Interaction Data"
            bulletPoints={[
              "Browser type, device type, anonymized IP address, and timestamps",
              "General interaction patterns (e.g., question categories, usage frequency)"
            ]}
          >
            To maintain and improve the platform, we may collect
            non-identifiable usage data, such as:
          </PolicySection>

          <OverviewText>
            This data is aggregated and cannot be traced back to individual
            users.
          </OverviewText>

          <PolicySection
            title="4. Cookies"
            bulletPoints={[
              "Maintain platform functionality",
              "Track anonymous usage for performance optimization"
            ]}
          >
            We may use essential, session-based cookies to:
          </PolicySection>

          <OverviewText>
            Cookies do not store personal or educational information. You may
            disable cookies via browser settings, though some features may be
            limited.
          </OverviewText>

          <PolicySection
            title="5. Third-Party Integrations"
          >
            If Campus Insights is integrated into a college or university
            website or system, any data exchange is controlled entirely by the
            institution. Campus Insights does not retain or repurpose any
            information received during these integrations.
          </PolicySection>

          <PolicySection
            title="6. Data Security"
          >
            Though we do not store personal data, we safeguard all anonymous
            interaction data using standard security practices. This includes
            encryption in transit and periodic security reviews to protect
            against unauthorized access.
          </PolicySection>

          <PolicySection
            title="7. International Access"
          >
            Campus Insights is governed by US law and FERPA. If accessed from
            other countries, we do not solicit personal data and are not
            subject to laws like GDPR. Institutions are responsible for any
            region-specific compliance.
          </PolicySection>

          <PolicySection
            title="8. Your Rights"
          >
            Since Campus Insights does not collect personal data, we cannot
            identify or retrieve individual user records. If you access Campus
            Insights through an institution, contact your institution to
            exercise data rights under FERPA or other laws.
          </PolicySection>

          <PolicySection
            title="9. Children's Privacy"
          >
            Campus Insights is intended for higher education. We do not
            knowingly collect information from children under 13. If students
            under 18 access the service, it is typically with institutional
            supervision and consent.
          </PolicySection>

          <PolicySection
            title="10. Updates to This Policy"
          >
            Campus Insights may update this Privacy Policy periodically. All
            changes will be posted on our website with the effective date
            clearly indicated.
          </PolicySection>

          <section>
            <OverviewHeading>11. Contact Us</OverviewHeading>
            <ContactText>
              Questions about privacy?
              <br />
              📧 Email: support@campusinsights.ai
              <br />
              🌐 Website: www.campusinsights.ai
            </ContactText>
          </section>
        </ContentContainer>
      </Card>
      </PageBackground>
       <CTASection />
      <Footer />
    </>
  );
}

const PageBackground = styled.div`
  height: 100%;
  width: 100%;
  background: linear-gradient(to bottom, #5e2bff 0px, #5e2bff 500px, #F5F5F9 100px, #F5F5F9 100%);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 200px 0;

  @media (max-width: 640px) {
    padding: 40px 0;
    background: linear-gradient(to bottom, #5e2bff 0px, #5e2bff 100px, #ffffff 100px, #ffffff 100%);
  }
`;

const BulletPoint = styled.li`
  width: 100%;
  color: #64636a;
  letter-spacing: -0.08px;
  padding-left: 20px;
  position: relative;
  font: 400 16px/160% Inter, -apple-system, Roboto, Helvetica, sans-serif;
  list-style: none;

  &::before {
    content: '•';
    position: absolute;
    left: 0;
  }

  @media (max-width: 991px) {
    font-size: 15px;
    line-height: 160%;
  }
  @media (max-width: 640px) {
    font-size: 14px;
    line-height: 160%;
    padding-left: 16px;
  }
`;

const SectionContainer = styled.section`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  width: 100%;
  @media (max-width: 640px) {
    gap: 16px;
  }
`;

const SectionHeading = styled.h2`
  width: 100%;
  color: #64636a;
  letter-spacing: -0.18px;
  font: 700 18px/140% Inter, -apple-system, Roboto, Helvetica, sans-serif;
  margin: 0;
  @media (max-width: 991px) {
    font-size: 16px;
    line-height: 140%;
  }
  @media (max-width: 640px) {
    font-size: 16px;
    line-height: 140%;
  }
`;

const SectionText = styled.p`
  width: 100%;
  color: #64636a;
  letter-spacing: -0.08px;
  font: 400 16px/160% Inter, -apple-system, Roboto, Helvetica, sans-serif;
  margin: 0;
  @media (max-width: 991px) {
    font-size: 15px;
    line-height: 160%;
  }
  @media (max-width: 640px) {
    font-size: 14px;
    line-height: 160%;
  }
`;

const BulletList = styled.ul`
  width: 100%;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PolicySection = ({ title, children, bulletPoints }) => {
  return (
    <SectionContainer>
      <SectionHeading>{title}</SectionHeading>
      {children && <SectionText>{children}</SectionText>}
      {bulletPoints && (
        <BulletList>
          {bulletPoints.map((point, index) => (
            <PolicyBulletPoint key={index}>{point}</PolicyBulletPoint>
          ))}
        </BulletList>
      )}
    </SectionContainer>
  );
};

const PolicyBulletPoint = ({ children }) => {
  return <BulletPoint>{children}</BulletPoint>;
};

export default PrivacyPolicy;
